// 0.12.11 Part B (as replaced by the addendum §1): what the client does when the backend answers
// "the database is unreachable".
//
// The backend uses ONE code for that, DB_UNAVAILABLE, on a 503 (apps/backend/src/lib/dbErrors.ts).
// It arrives as error.code in the typed envelope (route()) or as a top-level `code` in the legacy
// { data, error: string } shape (the global error handler). Only that explicit code switches paths:
// any other 5xx, incl. the dev proxy's 500 when the backend is down (S29 Part C), is untouched.
//
// A WRITE with this answer may or may not have committed. Every booking write is all-or-nothing up to
// its COMMIT (the rule above bookingTx), but a connection can drop during the COMMIT itself, and then
// nobody can know. So the message never says "not saved": it says the change could not be CONFIRMED,
// the form keeps what the user typed, nothing is retried or queued, and the board refreshes itself
// in the background (DB_UNAVAILABLE_WRITE_EVENT, handled in OpsBoard) so the truth shows up as soon
// as the database answers.
//
// Pure (no browser, no import.meta) so database/verify-db-unavailable.ts can load it in Node.

export const DB_UNAVAILABLE = 'DB_UNAVAILABLE';
export const DB_UNAVAILABLE_SAVE_MESSAGE = "Couldn't confirm your change was saved. The database was briefly unavailable. Check the board before trying again.";
export const DB_UNAVAILABLE_READ_MESSAGE = 'The database is briefly unavailable. Showing saved data where this device has it.';
// Dispatched on window by apiFetch after a write gets DB_UNAVAILABLE; the board refreshes on it.
export const DB_UNAVAILABLE_WRITE_EVENT = 'avsuite:db-unavailable-write';

//   'none'          — not a DB_UNAVAILABLE answer: existing handling.
//   'offline-read'  — a GET: take the offline read path, exactly as a network failure.
//   'write-failed'  — anything else: an ordinary error, never queued for replay, never a save.
export type DbUnavailableAction = 'none' | 'offline-read' | 'write-failed';

export function dbUnavailableAction(status: number, method: string, errorCode: unknown, topLevelCode: unknown): DbUnavailableAction {
  if (status !== 503 || (errorCode !== DB_UNAVAILABLE && topLevelCode !== DB_UNAVAILABLE)) return 'none';
  return method.toUpperCase() === 'GET' ? 'offline-read' : 'write-failed';
}
